import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { initialPath } from "~misc/paths";
import LoaderFull from "~components/Form/components/LoaderFull";
import ReportFilterBar from "../../components/ReportFilterBar";
import ReportListings from "../../components/ReportListings";
import { reportListingTypes } from "../../components/reportListingTypes";
import StatsCard from "../../components/StatsCard";
import { defaultReportSections, reportSectionIds } from "../../components/StatsCard.data";
import useLoadPositionData from "../../hooks/useLoadPositionData";
import { meses } from "../../utils/formatters";
import useVendedor from "~fetchs/useVendedor";



const statusEntregaOptions = {
  1: { key: "dentro-do-prazo", label: "Dentro do prazo",          type: "ok"   },
  2: { key: "atraso",          label: "Atraso acima do esperado", type: "late" },
};

const statusPedidoOptions = {
  1: { key: "em-separacao",            label: "Em separação"            },
  2: { key: "aguardando-carregamento", label: "Aguardando carregamento" },
  3: { key: "em-transporte",           label: "Em transporte"           },
};

const semStatus = { key: "sem-status", label: "Sem status", type: "ok" };

const toFilterOptions = (options) => [
  { value: "todos", label: "Todos" },
  ...Object.values(options).map(({ key, label }) => ({ value: key, label })),
];

const statusEntregaFilterOptions = toFilterOptions(statusEntregaOptions);
const statusPedidoFilterOptions  = toFilterOptions(statusPedidoOptions);

const orderOptions = [
  { value: "alfabetica",    label: "Ordem alfabética"     },
  { value: "mais-antigos",  label: "Pedidos mais antigos"  },
  { value: "mais-recentes", label: "Pedidos mais recentes" },
];



const getMonthSortValue = (mes) => {
  if (typeof mes !== "string") return 0;
  const [month, year] = mes.split("/");
  const monthNumber = Number(month);
  const yearNumber  = Number(year);
  if (!monthNumber || !yearNumber) return 0;
  return yearNumber * 100 + monthNumber;
};

const formatMonthPeriod = (mes) => {
  if (!mes || typeof mes !== "string") return mes;
  const parts = mes.split("/");
  if (parts.length !== 2) return mes;
  const [month, year] = parts;
  const monthNumber = Number(month);
  if (!monthNumber || monthNumber < 1 || monthNumber > 12) return mes;
  return `${meses[monthNumber - 1]}/${year}`;
};

const formatDateBr = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("pt-BR");
};

const formatDateTimeBr = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const hora = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return `${date.toLocaleDateString("pt-BR")} - ${hora}`;
};

const formatPercent = (value) => {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  const asPercent = value * 100;
  const rounded = Math.abs(asPercent) >= 10 ? asPercent.toFixed(0) : asPercent.toFixed(1);
  return `${asPercent > 0 ? "+" : ""}${rounded}% vs M-1`;
};



const normalizeFlatCodedItems = (items) => {
  const getMesFromDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${m}/${d.getFullYear()}`;
  };

  const getItemKey     = (item) => item?.codigo || item?.tag;
  const cardRota       = items.find((item) => getItemKey(item) === "Card-Rota");
  const cardTodos      = items.find((item) => getItemKey(item) === "Card-Todos");
  const rotaPositions  = items.filter((item) => getItemKey(item) === "Rota");
  const todosPositions = items.filter((item) => getItemKey(item) === "Todos");

  const groups = [];

  if (rotaPositions.length > 0) {
    const rotaMes = getMesFromDate(rotaPositions[0]?.dataPedido);
    if (rotaMes) {
      groups.push({ mes: rotaMes, card: cardRota?.card || null, posicoes: rotaPositions });
    }
  }

  if (todosPositions.length > 0) {
    groups.push({ mes: "todos", card: cardTodos?.card || null, posicoes: todosPositions });
  }

  return groups;
};

const normalizeVendedorGroupedItems = (vendedorItems, vendedorSelecionado = null) => {
  const getMesFromDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${m}/${d.getFullYear()}`;
  };

  const filtered = vendedorSelecionado !== null
    ? vendedorItems.filter((v) => v?.id === vendedorSelecionado)
    : vendedorItems;

  const rotaPositions = filtered.flatMap(
    (v) => (v?.basesPosicaoCarga || []).filter((item) => item?.tag === "Rota"),
  );
  const todosPositions = filtered.flatMap(
    (v) => (v?.basesPosicaoCarga || []).filter((item) => item?.tag === "Todos"),
  );

  const groups = [];

  if (rotaPositions.length > 0) {
    const rotaMes = getMesFromDate(rotaPositions[0]?.dataPedido);
    if (rotaMes) {
      groups.push({ mes: rotaMes, card: null, posicoes: rotaPositions });
    }
  }

  if (todosPositions.length > 0) {
    groups.push({ mes: "todos", card: null, posicoes: todosPositions });
  }

  return groups;
};

const normalizeRawGroups = (payload, vendedorSelecionado = null) => {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    const isVendedorGrouped = payload.some((item) => Array.isArray(item?.basesPosicaoCarga));
    if (isVendedorGrouped) return normalizeVendedorGroupedItems(payload, vendedorSelecionado);

    const isFlatCodedFormat = payload.some((item) => (item?.codigo || item?.tag) && !item?.mes);
    if (isFlatCodedFormat) return normalizeFlatCodedItems(payload);

    return payload.flatMap((group) => normalizeRawGroups(group, vendedorSelecionado)).filter(Boolean);
  }

  if (payload?.isSuccess && payload?.data !== undefined) return normalizeRawGroups(payload.data, vendedorSelecionado);
  if (payload?.mes) return [payload];
  if (!Array.isArray(payload?.data)) return [];

  return normalizeRawGroups(payload.data, vendedorSelecionado);
};

const isTodosMonth = (item) => String(item?.mes || "").trim().toLowerCase() === "todos";



const mapStatusFromPosition = (position) => {
  const entrega = statusEntregaOptions[Number(position?.statusPrazo)]   || semStatus;
  const pedido  = statusPedidoOptions[Number(position?.statusEntrega)] || semStatus;

  return {
    isAtrasado:        entrega.type === "late",
    statusEntregaKey:  entrega.key,
    statusPedidoKey:   pedido.key,
    statusEntrega:     { type: entrega.type ?? "ok", label: entrega.label },
    statusPedidoLabel: pedido.label,
  };
};



const buildCards = (cardData, firstTitle) => [
  {
    title:    firstTitle,
    value:    `${cardData?.totalPedidos ?? 0} pedidos`,
    subtitle: formatPercent(cardData?.percentTotalPedidos),
    color:    "navy",
  },
  {
    title:       "Total de pedidos em transito",
    value:       `${cardData?.totalEmTransito ?? 0} pedidos`,
    color:       "amber",
    filterKey:   "em-transporte",
    filterField: "statusPedido",
  },
  {
    title:       "Total de pedidos atrasados",
    value:       `${cardData?.totalEmAtraso ?? 0} pedidos`,
    color:       "red",
    filterKey:   "atraso",
    filterField: "statusEntrega",
  },
];

const buildCardDataFromPositions = (positions = [], fallbackCard = null) => {
  const mapped = positions.map(mapStatusFromPosition);

  return {
    totalPedidos: typeof fallbackCard?.totalPedidos === "number"
      ? fallbackCard.totalPedidos
      : positions.length,
    totalEmTransito:     mapped.filter((item) => item.statusPedidoKey === "em-transporte").length,
    totalEmAtraso:       mapped.filter((item) => item.isAtrasado).length,
    percentTotalPedidos: fallbackCard?.percentTotalPedidos ?? null,
  };
};

const getPositionDedupeKey = (position) => {
  const pedido  = position?.pedido ?? "";
  const data    = position?.dataPedido ?? "";
  const cliente =
    position?.cliente?.codigo ?? position?.codigoCliente ?? position?.clienteId ?? "";
  return `${pedido}|${data}|${cliente}`;
};

const dedupePositions = (positions = []) => {
  const seen = new Set();
  return positions.filter((position) => {
    const key = getPositionDedupeKey(position);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const mapPositionsToRows = (positions = []) =>
  positions.map((position, index) => {
    const { isAtrasado, statusEntregaKey, statusPedidoKey, statusEntrega, statusPedidoLabel } =
      mapStatusFromPosition(position);

    return {
      id:                      `${position.id || position.pedido || "pedido"}-${index}`,
      statusEntrega,
      isAtrasado,
      statusEntregaKey,
      statusPedidoKey,
      pedido:                  position.pedido || "-",
      dataCriacao:             formatDateBr(position.dataPedido),
      dataCriacaoRaw:          position.dataPedido,
      cliente:                 `${position?.cliente?.codigo || position?.codigoCliente || position?.clienteId || "-"} - ${position?.cliente?.razaoSocial || position?.razaoSocial || "-"}`,
      previsaoEntrega:         formatDateBr(position.previsaoEntrega),
      previsaoEntregaAjustada: formatDateBr(position.previsaoEntregaAjustada),
      statusPedido:            statusPedidoLabel,
      createdAt:               position.createdAt,
    };
  });



const normalizeRawPayload = (payload, vendedorSelecionado = null) => {
  const groups = normalizeRawGroups(payload, vendedorSelecionado);
  if (!groups.length) return null;

  const monthlyGroups = groups.filter((item) => !isTodosMonth(item));
  const latestMonthly =
    monthlyGroups
      .slice()
      .sort((a, b) => {
        const monthDiff = getMonthSortValue(b?.mes) - getMonthSortValue(a?.mes);
        if (monthDiff !== 0) return monthDiff;
        return Number(b?.id || 0) - Number(a?.id || 0);
      })[0] ??
    monthlyGroups.at(-1) ??
    null;

  const allMonths   = groups.find((item) => isTodosMonth(item)) || null;
  const cardsSource = latestMonthly || allMonths;
  if (!cardsSource) return null;

  const rotaRows       = mapPositionsToRows(latestMonthly?.posicoes || []);
  const todosPositions = dedupePositions([
    ...(latestMonthly?.posicoes || []),
    ...(allMonths?.posicoes    || []),
  ]);
  const todosRows = mapPositionsToRows(todosPositions);

  const routeCardData = buildCardDataFromPositions(
    latestMonthly?.posicoes || [],
    latestMonthly?.card || cardsSource.card,
  );
  const todosCardData = buildCardDataFromPositions(
    todosPositions,
    allMonths?.card || cardsSource.card,
  );

  const routeCards = buildCards(routeCardData, "Pedidos da rota");
  const allCards   = allMonths ? buildCards(todosCardData, "Todos os pedidos") : [];

  const updatedAtIso = todosRows
    .map((item) => item.createdAt)
    .filter(Boolean)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

  return {
    sections: [
      {
        id:     reportSectionIds.posicaoCarga,
        title:  "Posicao de carga",
        period: formatMonthPeriod(cardsSource?.mes) || "-",
        actions: [
          {
            key:    "rota",
            label:  "Pedidos da rota",
            active: true,
            period: formatMonthPeriod(cardsSource?.mes) || "-",
            cards:  routeCards,
          },
          ...(allCards.length
            ? [{ key: "todos", label: "Todos os pedidos", active: false, period: null, cards: allCards }]
            : []),
        ],
        cards: routeCards,
      },
    ],
    listingsByType: {
      [reportListingTypes.posicaoCarga]: {
        rows: rotaRows,
        rowsByAction: { rota: rotaRows, todos: todosRows },
      },
    },
    updatedAt: formatDateTimeBr(updatedAtIso),
  };
};

const normalizeLoadPositionSection = (section) => {
  if (section?.id !== reportSectionIds.posicaoCarga) return section;

  const actions = Array.isArray(section.actions) ? section.actions : [];

  const actionPedidosRota =
    actions.find((action) => String(action?.key   || "").toLowerCase() === "rota") ||
    actions.find((action) => String(action?.label || "").toLowerCase().includes("rota")) ||
    actions[0] ||
    null;

  const monthlySource =
    actionPedidosRota?.meses  ||
    actionPedidosRota?.months ||
    section?.meses            ||
    section?.months           ||
    [];

  const latestMonth = monthlySource.at(-1) ?? null;

  const cardsFromLatestMonth =
    latestMonth?.cards || latestMonth?.itens || latestMonth?.indicadores || null;

  const cardsPedidosRota =
    (Array.isArray(cardsFromLatestMonth) && cardsFromLatestMonth.length > 0
      ? cardsFromLatestMonth
      : actionPedidosRota?.cards) ||
    section.cards ||
    [];

  const periodFromLatestMonth = latestMonth?.period || latestMonth?.mes || latestMonth?.month;

  return {
    ...section,
    period:  periodFromLatestMonth || section.period,
    actions: [
      {
        ...(actionPedidosRota || {}),
        key:    "rota",
        label:  actionPedidosRota?.label || "Pedidos da rota",
        active: true,
        cards:  cardsPedidosRota,
      },
    ],
    cards: cardsPedidosRota,
  };
};



const LoadPositionPage = () => {
  const { payload, loading, isRefreshing, isMock } = useLoadPositionData();
  const { vendedorSelecionado } = useVendedor();

  const [searchValue,     setSearchValue]     = useState("");
  const [orderValue,      setOrderValue]      = useState("mais-antigos");
  const [statusEntrega,   setStatusEntrega]   = useState("todos");
  const [statusPedido,    setStatusPedido]    = useState("todos");
  const [dataCriacao,     setDataCriacao]     = useState("");
  const [lastVendedor,    setLastVendedor]    = useState(() => vendedorSelecionado);

  const { aba } = useParams();
  const navigate = useNavigate();
  const activeActionKey = aba === "todos" ? "todos" : "rota";

  const setActiveActionKey = useCallback(
    (key, { replace = false } = {}) => {
      const base = `${initialPath}/gestao-de-vendas/posicao-de-carga`;
      navigate(key === "todos" ? `${base}/todos` : base, { replace });
    },
    [navigate]
  );

  const autoSwitchedRef = useRef(false);
  const vendedorAnteriorRef = useRef(vendedorSelecionado);

  useEffect(() => {
    if (vendedorAnteriorRef.current === vendedorSelecionado) return;
    vendedorAnteriorRef.current = vendedorSelecionado;

    setLastVendedor(vendedorSelecionado);
    setActiveActionKey("rota", { replace: true });
    setSearchValue("");
    setStatusEntrega("todos");
    setStatusPedido("todos");
    setDataCriacao("");
    autoSwitchedRef.current = false;
  }, [vendedorSelecionado, setActiveActionKey]);

  const filterFields = useMemo(
    () => [
      {
        id:       "status-entrega",
        label:    "Status da entrega",
        value:    statusEntrega,
        onChange: setStatusEntrega,
        options:  statusEntregaFilterOptions,
      },
      {
        id:       "status-pedido",
        label:    "Status do pedido",
        value:    statusPedido,
        onChange: setStatusPedido,
        options:  statusPedidoFilterOptions,
      },
      {
        id:       "data-criacao",
        label:    "Data de criação do pedido",
        type:     "date",
        value:    dataCriacao,
        onChange: setDataCriacao,
      },
    ],
    [dataCriacao, statusEntrega, statusPedido],
  );

  const clearFilters = useCallback(() => {
    setSearchValue("");
    setOrderValue("mais-antigos");
    setStatusEntrega("todos");
    setStatusPedido("todos");
    setDataCriacao("");
  }, []);

  const normalizedPayload = useMemo(() => {
    if (Array.isArray(payload?.sections) || payload?.listingsByType || payload?.listings) {
      return {
        sections:       Array.isArray(payload?.sections)
          ? payload.sections.map(normalizeLoadPositionSection)
          : null,
        listingsByType: payload?.listingsByType || payload?.listings || null,
        updatedAt:      payload?.updatedAt,
      };
    }
    return normalizeRawPayload(payload, vendedorSelecionado);
  }, [payload, vendedorSelecionado]);

  const rawRows = useMemo(() => {
    if (lastVendedor !== vendedorSelecionado) return [];
    const byType = normalizedPayload?.listingsByType?.[reportListingTypes.posicaoCarga];
    return byType?.rowsByAction?.[activeActionKey] || byType?.rows || [];
  }, [normalizedPayload, activeActionKey, lastVendedor, vendedorSelecionado]);

  const filteredRows = useMemo(() => {
    let rows = [...rawRows];

    if (searchValue.trim()) {
      const q = searchValue.trim().toLowerCase();
      rows = rows.filter((row) => {
        const cliente = String(row.cliente || "").toLowerCase();
        const pedido  = String(row.pedido  || "").trim().toLowerCase();
        return cliente.includes(q) || pedido.includes(q);
      });
    }

    if (statusEntrega !== "todos") {
      rows = rows.filter((row) => row.statusEntregaKey === statusEntrega);
    }

    if (statusPedido !== "todos") {
      rows = rows.filter((row) => row.statusPedidoKey === statusPedido);
    }

    if (dataCriacao) {
      rows = rows.filter((row) => {
        if (!row.dataCriacaoRaw) return false;
        const d = new Date(row.dataCriacaoRaw);
        const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return localDate === dataCriacao;
      });
    }

    if (orderValue === "alfabetica") {
      rows.sort((a, b) => {
        const nomeA = String(a.cliente || "").split(" - ").slice(1).join(" - ").trim();
        const nomeB = String(b.cliente || "").split(" - ").slice(1).join(" - ").trim();
        return (nomeA || "").localeCompare(nomeB || "", "pt-BR", { sensitivity: "base" });
      });
      return rows;
    }

    rows.sort((a, b) => {
      const dateA = a.dataCriacaoRaw ? new Date(a.dataCriacaoRaw).getTime() : 0;
      const dateB = b.dataCriacaoRaw ? new Date(b.dataCriacaoRaw).getTime() : 0;
      return orderValue === "mais-recentes" ? dateB - dateA : dateA - dateB;
    });

    return rows;
  }, [dataCriacao, orderValue, rawRows, searchValue, statusEntrega, statusPedido]);

  const rowsByAction =
    normalizedPayload?.listingsByType?.[reportListingTypes.posicaoCarga]?.rowsByAction;
  const rotaRowsCount  = rowsByAction?.rota?.length  ?? 0;
  const todosRowsCount = rowsByAction?.todos?.length ?? 0;
  const isRotaEmpty    = rotaRowsCount === 0;
  const hasTodosRows   = todosRowsCount > 0;
  const sectionHasTodos = useMemo(() => {
    const section = normalizedPayload?.sections?.find(
      (s) => s.id === reportSectionIds.posicaoCarga,
    );
    return !!section?.actions?.some((a) => a.key === "todos");
  }, [normalizedPayload]);

  useEffect(() => {
    if (!normalizedPayload) return;
    if (autoSwitchedRef.current) return;
    if (
      activeActionKey === "rota" &&
      isRotaEmpty &&
      hasTodosRows &&
      sectionHasTodos
    ) {
      autoSwitchedRef.current = true;
      setActiveActionKey("todos", { replace: true });
    }
  }, [normalizedPayload, activeActionKey, isRotaEmpty, hasTodosRows, sectionHasTodos, setActiveActionKey]);

  const showRotaEmptyNotice =
    activeActionKey === "rota" && isRotaEmpty && sectionHasTodos;

  const listingFromApi = useMemo(() => ({
    ...(normalizedPayload?.listingsByType || {}),
    [reportListingTypes.posicaoCarga]: {
      ...(normalizedPayload?.listingsByType?.[reportListingTypes.posicaoCarga] || {}),
      rows: filteredRows,
    },
  }), [filteredRows, normalizedPayload]);

  return (
    <div className="space-y-4">
      {loading && !payload && (
        <div className="container rounded-md bg-white py-6">
          <LoaderFull />
        </div>
      )}

      {isRefreshing && (
        <div className="container">
          <div className="w-[130px] flex mt-4 items-center gap-2 bg-[#01054C] rounded-md px-4 py-2 text-xs font-semibold text-[#fff] animate-pulse">
            <span className="rounded-full" />
            Atualizando...
          </div>
        </div>
      )}

      <StatsCard
        sections={normalizedPayload?.sections || (!payload ? defaultReportSections : [])}
        sectionId={reportSectionIds.posicaoCarga}
        activeActionKey={activeActionKey}
        onActionChange={(_sectionId, actionKey) => {
          setActiveActionKey(actionKey);
          setSearchValue("");
          setStatusEntrega("todos");
          setStatusPedido("todos");
          setDataCriacao("");
        }}
        onCardClick={(card) => {
          if (!card.filterKey) return;
          if (card.filterField === "statusPedido") setStatusPedido(card.filterKey);
          else setStatusEntrega(card.filterKey);
        }}
      />

      {showRotaEmptyNotice && (
        <div className="container">
          <div className="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-[#1A2B49] md:flex-row md:items-center md:justify-between">
            <span>
              Não há pedidos na rota no momento.
            </span>
            <button
              type="button"
              onClick={() => setActiveActionKey("todos")}
              className="self-start rounded-md border border-[#0655A3] bg-white px-3 py-1.5 text-xs font-semibold text-[#0655A3] hover:bg-[#0655A3] hover:text-white md:self-auto"
            >
              Ver todos os pedidos
            </button>
          </div>
        </div>
      )}

      <ReportFilterBar
        searchValue={searchValue}
        onSearchValueChange={setSearchValue}
        onSearchSubmit={setSearchValue}
        filterFields={filterFields}
        onClearFilters={clearFilters}
        orderValue={orderValue}
        onOrderChange={setOrderValue}
        orderOptions={orderOptions}
        orderPlacement="bottom"
      />

      <ReportListings
        type={reportListingTypes.posicaoCarga}
        dataByType={listingFromApi}
        updatedAt={normalizedPayload?.updatedAt || "-"}
        footerNote="Atualização de segunda a sábado de hora em hora das 08:00 às 19:00"
      />
    </div>
  );
};

export default LoadPositionPage;